"use client";

import Link from "next/link";
import type { ReactNode } from "react";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export type OwnerFaqItem = {
  question: string;
  answer: ReactNode; // string or JSX (inline links)
};

export type OwnerFaqProps = {
  items: OwnerFaqItem[]; // component renders items.slice(0, 4)
  heading?: string; // defaults to "Frequently Asked Questions"
};

const INLINE_LINK_CLASS =
  "text-[#f45c24] underline decoration-[#f45c24]/40 underline-offset-2 hover:decoration-[#f45c24] transition-colors";

type KnownForLink = {
  label: string;
  href: string;
};

// Each label maps to a REAL /best/ guide href from the Tuscan localGuideLinks.
// No dedicated wings/patio/cocktails guides exist, so those reuse the closest guide.
const KNOWN_FOR_LINKS: KnownForLink[] = [
  { label: "Pizza", href: "/best/pizza-southern-highlands-las-vegas" },
  { label: "Wings", href: "/best/restaurants-southern-highlands-las-vegas" },
  { label: "Smash Burgers", href: "/best/burgers-southern-highlands-las-vegas" },
  { label: "Cocktails", href: "/best/happy-hour-southern-highlands-las-vegas" },
  { label: "Patio", href: "/best/restaurants-southern-highlands-las-vegas" },
  { label: "Brunch", href: "/best/brunch-southern-highlands-las-vegas" },
  { label: "Happy Hour", href: "/best/happy-hour-southern-highlands-las-vegas" },
];

function KnownForAnswer(): React.JSX.Element {
  return (
    <p>
      We are best known for our{" "}
      {KNOWN_FOR_LINKS.map((link, index) => {
        const isLast = index === KNOWN_FOR_LINKS.length - 1;
        const isSecondToLast = index === KNOWN_FOR_LINKS.length - 2;
        return (
          <span key={link.label}>
            <Link
              href={link.href}
              className={INLINE_LINK_CLASS}
              data-track="menu_clicked"
              data-track-location="home_owner_faq_known_for"
              data-track-label={link.label}
            >
              {link.label}
            </Link>
            {isLast ? "." : isSecondToLast ? ", and " : ", "}
          </span>
        );
      })}
    </p>
  );
}

function OrderOnlineAnswer(): React.JSX.Element {
  return (
    <p>
      Yes. You can{" "}
      <Link
        href="/order"
        className={INLINE_LINK_CLASS}
        data-track="order_started"
        data-track-location="home_owner_faq"
      >
        order online for pickup or delivery
      </Link>{" "}
      directly through our website.
    </p>
  );
}

export const OWNER_FAQ_FALLBACK: OwnerFaqItem[] = [
  {
    question: "What are you known for?",
    answer: <KnownForAnswer />,
  },
  {
    question: "What meals do you serve?",
    answer:
      "We serve dinner Monday through Saturday from 4 to 9 PM and Sunday brunch from 10 AM to 8 PM. Happy hour runs daily from 4 to 6 PM.",
  },
  {
    question: "Can I order online for pickup or delivery?",
    answer: <OrderOnlineAnswer />,
  },
  {
    question: "Where are you located and how do I get in?",
    answer:
      "Tuscan Cove Bar & Patio is at 12656 Southern Highlands Pkwy, Las Vegas, NV 89141, inside the Tuscan Highlands community. Dial gate code 418 at the call box and follow the signs to Tuscan Cove parking. Call (725) 223-0714 if you need help.",
  },
];

export function OwnerFaq({
  items,
  heading = "Frequently Asked Questions",
}: OwnerFaqProps): React.JSX.Element {
  const faqItems = items.slice(0, 4);

  return (
    <section
      data-section="owner-faq"
      aria-labelledby="owner-faq-heading"
      className="font-system bg-white px-4 py-14 md:px-6 md:py-20"
    >
      <div className="mx-auto max-w-3xl">
        <h2
          id="owner-faq-heading"
          className="font-system text-[#1a1a1a] font-bold text-[clamp(1.625rem,4vw,2rem)] leading-tight tracking-tight"
        >
          {heading}
        </h2>

        <Accordion type="single" collapsible className="mt-6 sm:mt-8">
          {faqItems.map((item, index) => (
            <AccordionItem
              key={index}
              value={`faq-${index}`}
              className="border-[#e5e5e5]"
            >
              <AccordionTrigger className="text-[15px] sm:text-base font-medium text-[#1a1a1a] hover:no-underline py-5">
                {item.question}
              </AccordionTrigger>
              <AccordionContent
                forceMount
                className="pb-5 pr-6 text-[15px] leading-relaxed text-neutral-600"
              >
                {item.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

export default OwnerFaq;
